import {
    FINANCIAL_PERIOD_LABELS,
    FinancialPeriod,
} from '@/lib/api/financial/financial-dashboard-api'
import { useEffect, useState } from 'react'
import { MdCheck, MdDateRange } from 'react-icons/md'

interface FinancialPeriodSelectorProps {
    value: FinancialPeriod
    onChange: (period: FinancialPeriod) => void
    startDate?: string
    endDate?: string
    onCustomDatesChange?: (startDate: string, endDate: string) => void
}

const periods: FinancialPeriod[] = [
    'today',
    'week',
    'month',
    'last_month',
    'quarter',
    'year',
    'custom',
]

export default function FinancialPeriodSelector({
    value,
    onChange,
    startDate = '',
    endDate = '',
    onCustomDatesChange,
}: FinancialPeriodSelectorProps) {
    // Rascunho: o calendário nativo troca o valor do campo já ao mudar de MÊS, antes
    // de o operador escolher o dia. Aplicar a cada mudança disparava uma busca por
    // clique de navegação. As datas só valem quando ele confirma.
    const [draftStart, setDraftStart] = useState(startDate)
    const [draftEnd, setDraftEnd] = useState(endDate)
    useEffect(() => setDraftStart(startDate), [startDate])
    useEffect(() => setDraftEnd(endDate), [endDate])

    const complete = !!draftStart && !!draftEnd
    const inverted = complete && draftStart > draftEnd
    const changed = draftStart !== startDate || draftEnd !== endDate
    const canApply = complete && !inverted && changed
    const apply = () => {
        if (canApply) onCustomDatesChange?.(draftStart, draftEnd)
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
                {periods.map(period => (
                    <button
                        key={period}
                        onClick={() => onChange(period)}
                        className={`btn btn-sm ${
                            value === period
                                ? 'btn-secondary'
                                : 'btn-ghost border border-base-300'
                        }`}
                    >
                        {period === 'custom' && (
                            <MdDateRange className="text-base" />
                        )}
                        {FINANCIAL_PERIOD_LABELS[period]}
                    </button>
                ))}
            </div>

            {value === 'custom' && (
                <div className="flex flex-wrap items-end gap-3 bg-base-100 border border-base-300 rounded-lg p-3">
                    <div className="form-control">
                        <label className="label py-1">
                            <span className="label-text text-xs font-medium">
                                Data inicial
                            </span>
                        </label>
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={draftStart}
                            max={draftEnd || undefined}
                            onChange={e => setDraftStart(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && apply()}
                        />
                    </div>
                    <div className="form-control">
                        <label className="label py-1">
                            <span className="label-text text-xs font-medium">
                                Data final
                            </span>
                        </label>
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={draftEnd}
                            min={draftStart || undefined}
                            onChange={e => setDraftEnd(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && apply()}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary gap-1"
                        disabled={!canApply}
                        onClick={apply}
                    >
                        <MdCheck className="text-base" />
                        Aplicar
                    </button>
                    {!complete && (
                        <span className="text-xs text-base-content/60 pb-2">
                            Selecione ambas as datas e clique em Aplicar.
                        </span>
                    )}
                    {inverted && (
                        <span className="text-xs text-error pb-2">
                            A data inicial não pode ser depois da final.
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}
